import { useRef, useState, type FormEvent } from "react"

import {
  internalCustomerCreateRequestV1Schema,
  internalCustomerDuplicatePreviewRequestV1Schema,
  internalCustomerInputV1Schema,
  type InternalCustomerDuplicatePreviewResponseV1,
  type InternalCustomerInputV1,
} from "@impetusos/contracts"

import {
  createInternalCustomer,
  previewInternalCustomerDuplicates,
} from "../transport/index.js"

type Draft = Omit<
  InternalCustomerInputV1,
  "customerId" | "contactId" | "serviceLocationId" | "billingProfileId"
> & { branchId: string; legalEntityId: string }

const initialDraft = (): Draft => ({
  branchId: "",
  legalEntityId: "",
  customerType: "residential",
  displayName: "",
  contactName: "",
  email: null,
  phone: null,
  emailConsent: false,
  smsConsent: false,
  addressLine1: "",
  addressLine2: null,
  city: "",
  region: "",
  postalCode: "",
  country: "US",
  timezone: "America/New_York",
  currency: "USD",
  desiredStatus: "Draft",
})

const newIds = () => ({
  customerId: crypto.randomUUID(),
  contactId: crypto.randomUUID(),
  serviceLocationId: crypto.randomUUID(),
  billingProfileId: crypto.randomUUID(),
})

export const InternalCustomerWizard = () => {
  const [draft, setDraft] = useState<Draft>(initialDraft)
  const ids = useRef<ReturnType<typeof newIds> | null>(null)
  if (ids.current === null) ids.current = newIds()
  const [workflow, setWorkflow] = useState<{
    review: InternalCustomerDuplicatePreviewResponseV1 | null
    confirmSeparate: boolean
    busy: "review" | "create" | null
    notice: string | null
  }>({ review: null, confirmSeparate: false, busy: null, notice: null })
  const { review, confirmSeparate, busy, notice } = workflow
  const updateWorkflow = (next: Partial<typeof workflow>) =>
    setWorkflow((current) => ({ ...current, ...next }))

  const customer = (): InternalCustomerInputV1 => internalCustomerInputV1Schema.parse({
    ...ids.current,
    customerType: draft.customerType,
    displayName: draft.displayName,
    contactName: draft.contactName,
    email: draft.email || null,
    phone: draft.phone || null,
    emailConsent: draft.emailConsent,
    smsConsent: draft.smsConsent,
    addressLine1: draft.addressLine1,
    addressLine2: draft.addressLine2 || null,
    city: draft.city,
    region: draft.region,
    postalCode: draft.postalCode,
    country: draft.country,
    timezone: draft.timezone,
    currency: draft.currency,
    desiredStatus: draft.desiredStatus,
  })

  const update = <Key extends keyof Draft>(key: Key, value: Draft[Key]) => {
    setDraft((current) => ({ ...current, [key]: value }))
    updateWorkflow({ review: null, confirmSeparate: false, notice: null })
  }

  const reviewDuplicates = async () => {
    updateWorkflow({ busy: "review", notice: null })
    try {
      const request = internalCustomerDuplicatePreviewRequestV1Schema.parse({
        requestVersion: 1,
        correlationId: crypto.randomUUID(),
        idempotencyKey: `customer-preview-${crypto.randomUUID()}`,
        branchId: draft.branchId,
        legalEntityId: draft.legalEntityId,
        customer: customer(),
      })
      const result = await previewInternalCustomerDuplicates(request)
      updateWorkflow({ review: result })
      if (result.status !== "reviewed") {
        updateWorkflow({ notice: "The duplicate review was not confirmed. Nothing was created." })
      }
    } catch {
      updateWorkflow({ notice: "Complete the required Customer, contact, address, Branch, and Legal Entity fields." })
    } finally {
      updateWorkflow({ busy: null })
    }
  }

  const create = async (event: FormEvent) => {
    event.preventDefault()
    if (review?.status !== "reviewed" || review.duplicateReviewFingerprint === null) {
      updateWorkflow({ notice: "Review duplicate candidates before creating this Customer." })
      return
    }
    if (review.candidateCustomerIds.length > 0 && !confirmSeparate) {
      updateWorkflow({ notice: "Resolve the duplicate warning before creating a separate Customer." })
      return
    }
    updateWorkflow({ busy: "create", notice: null })
    try {
      const result = await createInternalCustomer(
        internalCustomerCreateRequestV1Schema.parse({
          requestVersion: 1,
          correlationId: crypto.randomUUID(),
          idempotencyKey: `customer-create-${crypto.randomUUID()}`,
          branchId: draft.branchId,
          legalEntityId: draft.legalEntityId,
          customer: customer(),
          duplicateReviewFingerprint: review.duplicateReviewFingerprint,
          duplicateDecision: review.candidateCustomerIds.length > 0
            ? "create_new"
            : "no_candidates",
        })
      )
      if (result.status === "confirmed") {
        updateWorkflow({ notice: `Customer ${result.customerId ?? ""} was confirmed at version ${result.version ?? 1}.` })
        setDraft(initialDraft())
        ids.current = newIds()
        updateWorkflow({ review: null, confirmSeparate: false })
      } else if (result.status === "conflict") {
        updateWorkflow({ review: null, notice: "Customer or duplicate data changed. Review candidates again." })
      } else {
        updateWorkflow({ notice: "The authoritative service did not confirm creation. Nothing is shown as complete." })
      }
    } catch {
      updateWorkflow({ notice: "Customer creation could not be confirmed. Review the fields and retry." })
    } finally {
      updateWorkflow({ busy: null })
    }
  }

  return (
    <section className="customer-wizard" id="customers" aria-labelledby="customer-wizard-title">
      <div className="wizard-heading">
        <div>
          <p className="eyebrow">Internal intake</p>
          <h2 id="customer-wizard-title">Create a Customer</h2>
        </div>
        <span className="step-badge">Duplicate review required</span>
      </div>
      {notice && <div className="notice" role="status"><span aria-hidden="true">!</span><p>{notice}</p></div>}
      <form onSubmit={(event) => { void create(event) }}>
        <fieldset>
          <legend>Customer and scope</legend>
          <label><span>Customer type</span><select value={draft.customerType} onChange={(event) => update("customerType", event.target.value as Draft["customerType"])}><option value="residential">Residential</option><option value="commercial">Commercial</option><option value="property_manager">Property manager</option></select></label>
          <label><span>Display name</span><input required value={draft.displayName} onChange={(event) => update("displayName", event.target.value)} /></label>
          <label><span>Owning Branch ID</span><input required value={draft.branchId} onChange={(event) => update("branchId", event.target.value)} /></label>
          <label><span>Legal Entity ID</span><input required value={draft.legalEntityId} onChange={(event) => update("legalEntityId", event.target.value)} /></label>
          <label><span>Initial state</span><select value={draft.desiredStatus} onChange={(event) => update("desiredStatus", event.target.value as Draft["desiredStatus"])}><option value="Draft">Save draft</option><option value="Active">Activate</option></select></label>
        </fieldset>
        <fieldset>
          <legend>Primary contact</legend>
          <label><span>Contact name</span><input required value={draft.contactName} onChange={(event) => update("contactName", event.target.value)} /></label>
          <label><span>Email</span><input type="email" value={draft.email ?? ""} onChange={(event) => update("email", event.target.value || null)} /></label>
          <label><span>Phone</span><input value={draft.phone ?? ""} onChange={(event) => update("phone", event.target.value || null)} /></label>
          <label className="check-field"><input type="checkbox" checked={draft.emailConsent} onChange={(event) => update("emailConsent", event.target.checked)} /><span>Email consent recorded</span></label>
          <label className="check-field"><input type="checkbox" checked={draft.smsConsent} onChange={(event) => update("smsConsent", event.target.checked)} /><span>SMS consent recorded</span></label>
        </fieldset>
        <fieldset>
          <legend>Service address</legend>
          <label><span>Address</span><input required value={draft.addressLine1} onChange={(event) => update("addressLine1", event.target.value)} /></label>
          <label><span>City</span><input required value={draft.city} onChange={(event) => update("city", event.target.value)} /></label>
          <label><span>State / region</span><input required value={draft.region} onChange={(event) => update("region", event.target.value)} /></label>
          <label><span>Postal code</span><input required value={draft.postalCode} onChange={(event) => update("postalCode", event.target.value)} /></label>
        </fieldset>
        <div className="duplicate-panel" aria-live="polite">
          <button type="button" className="secondary-button" disabled={busy !== null} onClick={() => { void reviewDuplicates() }}>{busy === "review" ? "Reviewing…" : "Review duplicate candidates"}</button>
          {review?.status === "reviewed" && review.candidateCustomerIds.length === 0 && <p className="duplicate-clear">No exact email, phone, or address candidates found.</p>}
          {review?.status === "reviewed" && review.candidateCustomerIds.length > 0 && (
            <div className="duplicate-warning" role="alert"><strong>Possible duplicate Customer</strong><p>Review these Tenant-wide records before continuing: {review.candidateCustomerIds.join(", ")}</p><label className="check-field"><input type="checkbox" checked={confirmSeparate} onChange={(event) => updateWorkflow({ confirmSeparate: event.target.checked })} /><span>I reviewed these candidates and intend to create a separate Customer.</span></label></div>
          )}
        </div>
        <div className="wizard-actions"><p>Creation is committed only after Frappe rechecks the duplicate fingerprint, Tenant, Branch, Legal Entity, and current Membership.</p><button className="primary-button" type="submit" disabled={busy !== null || review?.status !== "reviewed"}>{busy === "create" ? "Awaiting confirmation…" : draft.desiredStatus === "Active" ? "Create and activate" : "Create draft"}</button></div>
      </form>
    </section>
  )
}
